import * as Yup from 'yup';
import { parsePhoneNumber } from 'libphonenumber-js/max';
import { isBefore, isAfter, addDays } from 'date-fns';
import {
  paymentMethod as PaymentMethod,
  policyType as PolicyType,
  soldToEffectiveDaysIntervalByState,
  standaloneRentersStates,
  poBoxPatterns
} from '@ourbranch/lookups';

import smartyStreetsAPI from 'core/helpers/smartyStreetsAPI';
import { driversLicenseNumberSchema } from 'core/helpers/drivers-license-number-validations';
import { validUSState, validMortgageDetails, validMortgageLoanNumber } from 'common/helpers/yup-helpers';
import preSaleChecklistSchema from './components/pre-sale-checklist/pre-sale-checklist.validation-schema';
import extraQuestionsSchema from './components/extra-questions/extra-questions.validation-schema';
import { hasPaymentMethod } from './helper';

Yup.addMethod(Yup.mixed, 'validUSState', validUSState);
Yup.addMethod(Yup.mixed, 'validMortgageDetails', validMortgageDetails);
Yup.addMethod(Yup.mixed, 'validMortgageLoanNumber', validMortgageLoanNumber);

const regexZipCode = new RegExp(/^[0-9]{5}(?:-[0-9]{4})?$/);

const buyValidationSchema = (values, policyType) =>
  Yup.object().shape({
    billingDayOfMonth: Yup.number().nullable().min(1).max(28).required('Billing day of month is required'),
    firstName: Yup.string().required('First name is required'),
    lastName: Yup.string().required('Last name is required'),
    email: Yup.string().trim().email().required('Email is required').nullable(),
    address: Yup.object()
      .shape({
        address: Yup.string()
          .required('Mailing address is required')
          .test('is-po-box', 'PO Box Addresses are invalid', (value) => {
            // Allow PO Box for Farm policies
            if (policyType === PolicyType.Farm || policyType === PolicyType.FABundle) {
              return true;
            }
            const isPrimaryPOBox = value && poBoxPatterns.some((pattern) => pattern.test(value));
            const isSecondaryPOBox =
              values.address?.address2 && poBoxPatterns.some((pattern) => pattern.test(values.address.address2));
            return !isPrimaryPOBox && !isSecondaryPOBox;
          }),
        address2: Yup.string()
          .nullable()
          .test(
            'check-for-apt-unit',
            'This address may have an apt, unit, floor, building, suite, etc.',
            async (value) => {
              // Early exit conditions to prevent unnecessary API calls
              if (!values.address?.address || !values.address?.city || !values.address?.state || !values.address?.zip) {
                return true;
              }

              const { needsUnitNumber } = await smartyStreetsAPI.validateAddress(values.address);
              if (needsUnitNumber && !value && !values.noAptNumber) {
                return false;
              }
              return true;
            }
          ),
        city: Yup.string().required('City is required'),
        state: Yup.string().required('State is required'),
        zip: Yup.string()
          .required('Zip is required')
          .test('invalid-zip-code', 'Invalid zip code', (zip) => {
            if (zip) {
              return regexZipCode.test(zip);
            }
            return true;
          })
      })
      .nullable(),
    phone: Yup.string()
      .required('Phone number is required')
      .test('is-valid-phone', 'Invalid phone number', (phone) => {
        try {
          return parsePhoneNumber(phone, { defaultCountry: 'US' }).isValid();
        } catch {
          return false;
        }
      })
      .nullable(),
    additionalPhoneNumbers: Yup.array()
      .of(
        Yup.object().shape({
          phoneNumber: Yup.string().required('Required or click trash to remove').min(10, 'Must be at least 10 digits'),
          note: Yup.string().max(50, 'Must be less than 50 characters').nullable()
        })
      )
      .nullable(),
    currentHomeownersCarrier: Yup.string().when('breakupWithHomeowners', {
      is: true,
      then: Yup.string().required('Please enter your current insurance company').nullable(),
      otherwise: Yup.string().nullable()
    }),
    currentAutoCarrier: Yup.string().when('breakupWithAuto', {
      is: true,
      then: Yup.string().required('Please enter your current insurance company').nullable(),
      otherwise: Yup.string().nullable()
    }),
    breakupESignature: Yup.string()
      .when('breakupWithAuto', {
        is: true,
        then: Yup.string().required(),
        otherwise: Yup.string().nullable()
      })
      .when('breakupWithHomeowners', {
        is: true,
        then: Yup.string().required(),
        otherwise: Yup.string().nullable()
      })
  });

const autoAttestationsSchema = Yup.object().shape({
  attestationsAutoAccepted: Yup.boolean()
    .required()
    .oneOf([true], 'Must click on checkbox to confirm attestation')
    .typeError('Must click on checkbox to confirm attestation')
});

const homeAttestationsSchema = Yup.object().shape({
  attestationsHomeAccepted: Yup.boolean()
    .required()
    .oneOf([true], 'Must click on checkbox to confirm attestation')
    .typeError('Must click on checkbox to confirm attestation')
});

const rentersAttestationsSchema = Yup.object().shape({
  attestationsRentersAccepted: Yup.boolean()
    .required()
    .oneOf([true], 'Must click on checkbox to confirm attestation')
    .typeError('Must click on checkbox to confirm attestation')
});

const condoAttestationsSchema = Yup.object().shape({
  attestationsCondoAccepted: Yup.boolean()
    .required()
    .oneOf([true], 'Must click on checkbox to confirm attestation')
    .typeError('Must click on checkbox to confirm attestation')
});

const cardValidationSchema = Yup.object().shape({
  completeCardData: Yup.boolean().required().oneOf([true], 'Credit card information is required')
});

const achValidationSchema = Yup.object().shape({
  isRoutingNumberValid: Yup.boolean().required().oneOf([true], 'Valid routing number of 9 digits is required'),
  isAccountNumberValid: Yup.boolean().required().oneOf([true], 'Account number is required'),
  isAccountHolderNameValid: Yup.boolean().required().oneOf([true], 'Account holder name is required')
});

const maxDate = new Date(new Date().setDate(new Date().getDate() + 59));

const autoValidationSchema = (daysOut) => {
  const minDate = new Date(new Date().setDate(new Date().getDate() + daysOut - 1));

  return Yup.object().shape({
    autoEffectiveDate: Yup.string()
      .nullable()
      .test('required', 'Policy start date is required', (obj) => !!obj)
      .test('min', `Policy start date must be at least ${daysOut} days in the future`, (obj) => {
        const date = new Date(obj);
        return date > minDate;
      })
      .test('max', 'Policy start date must be within the next 60 days', (obj) => {
        const date = new Date(obj);
        return date < maxDate;
      }),
    autoPaymentType: Yup.string().required('Payment type is required'),
    autoPaymentMethod: Yup.string().required('Payment method is required'),
    drivers: Yup.array()
      .of(
        Yup.object().shape({
          driversLicenseState: Yup.string().required("Driver's license state is required."),
          driversLicenseNumber: driversLicenseNumberSchema.required("Driver's license number is required.")
        })
      )
      .nullable(),
    cars: Yup.array()
      .of(
        Yup.object().shape({
          make: Yup.string().test(
            'make-must-not-start-with-an-asterisk',
            'This car entry was made by a comparative rater without a valid VIN. Please add a new car with the correct VIN and remove the car with the invalid VIN to continue.',
            (value) => {
              return !String(value).startsWith('*');
            }
          )
        })
      )
      .nullable()
  });
};

const homeValidationSchema = (minDateHome, daysOut, overridesForFeFormValidation) => {
  return Yup.object().shape({
    homeEffectiveDate: Yup.string()
      .nullable()
      .test('required', 'Policy start date is required', (obj) => !!obj)
      .test('min', `Policy start date must be at least ${daysOut} days in the future`, (obj) => {
        const date = new Date(obj);
        return isBefore(minDateHome, date);
      })
      .test('max', 'Policy start date must be within the next 60 days', (obj) => {
        const date = new Date(obj);
        return date < maxDate;
      })
      .test('required', 'Mortgage lender name is required', function (obj) {
        return this.options.context.homeownersPaymentMethod !== 'W' || (!!obj && obj.length > 0);
      }),
    homeownersPaymentType: Yup.string().required('Payment type is required'),
    homeownersPaymentMethod: Yup.string().required('Payment method is required'),
    mortgageDetails: Yup.object()
      .when('homeownersPaymentMethod', {
        is: (homeownersPaymentMethod) => homeownersPaymentMethod === 'W',
        then: Yup.object().shape({
          mortgageHolderName: Yup.string().validMortgageDetails('mortgageHolderName'),
          loanNumber: Yup.string()
            .validMortgageDetails('loanNumber')
            .typeError('Loan number is required')
            .validMortgageLoanNumber(overridesForFeFormValidation),
          mortgageHolderAddress: Yup.object()
            .shape({
              address: Yup.string()
                .validMortgageDetails('mortgageHolderAddress.address')
                .typeError('Mortgage lender address must be fully filled in'),
              address2: Yup.string().nullable(),
              city: Yup.string()
                .validMortgageDetails('mortgageHolderAddress.city')
                .typeError('Mortgage lender address must be fully filled in'),
              state: Yup.string()
                .validUSState('mortgageHolderAddress.state')
                .validMortgageDetails('mortgageHolderAddress.state')
                .typeError('Mortgage lender address must be fully filled in'),
              zip: Yup.string()
                .validMortgageDetails('mortgageHolderAddress.zip')
                .typeError('Mortgage lender address must be fully filled in')
            })
            .typeError('Mortgage lender address must be fully filled in')
        }),
        otherwise: Yup.object().nullable()
      })
      .nullable(),
    loanOfficerEmail: Yup.string().email('Please use a valid email').nullable()
  });
};

const condoValidationSchema = (minDateHome, state) => {
  return Yup.object().shape({
    condoEffectiveDate: Yup.string()
      .nullable()
      .test('required', 'Policy start date is required', (obj) => !!obj)
      .test(
        'min',
        `Policy start date must be at least ${soldToEffectiveDaysIntervalByState(state, PolicyType.Condo)} days in the future`,
        (obj) => {
          const date = new Date(obj);
          return isBefore(minDateHome, date);
        }
      )
      .test('max', 'Policy start date must be within the next 60 days', (obj) => {
        const date = new Date(obj);
        return date < maxDate;
      })
      .test('required', 'Mortgage lender name is required', function (obj) {
        return this.options.context.homeownersPaymentMethod !== 'W' || (!!obj && obj.length > 0);
      }),
    condoPaymentType: Yup.string().required('Payment type is required'),
    condoPaymentMethod: Yup.string().required('Payment method is required'),
    mortgageDetails: Yup.object().shape({
      mortgageHolderName: Yup.string()
        .nullable()
        .test('required', 'Mortgage lender name is required. Go back to edit offer to add a name', function (obj) {
          return (
            !this.options.context?.selectedOption?.includes('H') ||
            this.options.context.homeownersPaymentMethod !== 'W' ||
            (!!obj && obj.trim().length > 0)
          );
        }),
      mortgageHolderAddress: Yup.object()
        .shape({
          address: Yup.string().nullable(),
          address2: Yup.string().nullable(),
          city: Yup.string().nullable(),
          state: Yup.string().ensure().validUSState('checkout.mortgageHolderAddress.state'),
          zip: Yup.string().nullable()
        })
        .nullable(),
      loanNumber: Yup.string().nullable(),
      mortgageHolderEmail: Yup.string().email('Please use a valid email').nullable(),
      mortgageHolderFax: Yup.string().nullable()
    }),
    loanOfficerEmail: Yup.string().email('Please use a valid email').nullable()
  });
};

function getPaymentSchema(policyType, quote, canClearUWFormRejections) {
  let schema = Yup.object().shape({});
  const {
    noBindHome,
    noBindAuto,
    correctedAddress,
    overrides: { overridesForFeFormValidation } = quote?.overrides || {}
  } = quote;

  if (policyType.includes(PolicyType.Home) && !noBindHome) {
    const daysOut = soldToEffectiveDaysIntervalByState(correctedAddress.state, PolicyType.Home);
    const dateDaysOut = addDays(new Date(), daysOut);
    const { purchaseDate } = quote.home;
    const purchaseDateToCompare = new Date(`${purchaseDate}T00:00`);
    const minDateHome =
      purchaseDateToCompare && isAfter(purchaseDateToCompare, dateDaysOut)
        ? purchaseDateToCompare.setDate(purchaseDateToCompare.getDate() - 1)
        : dateDaysOut.setDate(dateDaysOut.getDate() - 1);
    schema = schema.concat(homeValidationSchema(minDateHome, daysOut, overridesForFeFormValidation));
  }

  if (policyType.includes(PolicyType.Condo)) {
    const dateDaysOut = addDays(
      new Date(),
      soldToEffectiveDaysIntervalByState(correctedAddress.state, PolicyType.Condo)
    );
    const { purchaseDate } = quote.condo;
    const purchaseDateToCompare = new Date(`${purchaseDate}T00:00`);
    const minDateCondo =
      purchaseDateToCompare && isAfter(purchaseDateToCompare, dateDaysOut)
        ? purchaseDateToCompare.setDate(purchaseDateToCompare.getDate() - 1)
        : dateDaysOut.setDate(dateDaysOut.getDate() - 1);
    schema = schema.concat(condoValidationSchema(minDateCondo));
  }

  if (!noBindAuto && policyType.includes(PolicyType.Auto)) {
    schema = schema.concat(
      autoValidationSchema(
        soldToEffectiveDaysIntervalByState(correctedAddress.state, PolicyType.Auto),
        quote,
        canClearUWFormRejections
      )
    );
  }

  return schema;
}

export function getSchema(policyType, quote, values, tasks, { isAgency, canBind, canClearUWFormRejections, offer }) {
  const { noBindHome, noBindAuto, correctedAddress } = quote;
  let baseBuySchema = buyValidationSchema(values, policyType);
  let paymentSchema = getPaymentSchema(policyType, quote, canClearUWFormRejections);
  if (
    canBind &&
    hasPaymentMethod({
      paymentMethods: quote.global,
      paymentMethod: PaymentMethod.CreditCard,
      policyType,
      noBindHome,
      noBindAuto
    })
  ) {
    paymentSchema = paymentSchema.concat(cardValidationSchema);
  }

  if (
    canBind &&
    hasPaymentMethod({
      paymentMethods: quote.global,
      paymentMethod: PaymentMethod.ACH,
      policyType,
      noBindHome,
      noBindAuto
    })
  ) {
    paymentSchema = paymentSchema.concat(achValidationSchema);
  }

  if (!noBindHome && policyType === PolicyType.Home) {
    baseBuySchema = baseBuySchema.concat(homeAttestationsSchema);
  }

  if ([PolicyType.CABundle, PolicyType.Condo].includes(policyType)) {
    baseBuySchema = baseBuySchema.concat(condoAttestationsSchema);
  }

  if (!noBindAuto && policyType.includes(PolicyType.Auto)) {
    baseBuySchema = baseBuySchema.concat(autoAttestationsSchema);
  }

  if (
    policyType === PolicyType.Renters ||
    (policyType === PolicyType.ARBundle && standaloneRentersStates[correctedAddress.state])
  ) {
    baseBuySchema = baseBuySchema.concat(rentersAttestationsSchema);
  }

  if (policyType === PolicyType.HABundle && !noBindHome && !noBindAuto) {
    baseBuySchema = baseBuySchema.concat(homeAttestationsSchema).concat(autoAttestationsSchema);
  }

  // Pre-sale checklist validation
  baseBuySchema = baseBuySchema.concat(preSaleChecklistSchema(policyType, quote, values, offer, tasks));

  // Extra questions validation
  baseBuySchema = baseBuySchema.concat(extraQuestionsSchema(quote, isAgency));

  // RC1 verified data validation
  baseBuySchema = baseBuySchema.concat(
    Yup.object().shape({
      usingNonVerifiedData: Yup.boolean()
        .nullable()
        .test('has-unverified-data', 'This offer has unverified data', function () {
          return !this.options.context.usingNonVerifiedData;
        })
    })
  );

  return baseBuySchema.concat(paymentSchema);
}
